import { z } from '@hono/zod-openapi'

/** UUID (v7 preferred; any RFC-4122 accepted on input). */
export const Uuid = z.string().uuid().openapi({ example: '01909b2e-7c2a-7b3a-9f5e-3d2b1a0c9d8e' })

/** ISO-8601 UTC timestamp string — the only timestamp wire format (API_SPEC_RULES §1). */
export const IsoDateTime = z
  .string()
  .datetime({ offset: true })
  .openapi({ example: '2026-07-06T14:00:00Z' })

/** Calendar date (no time), for columns ending `_on`. */
export const IsoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD')
  .openapi({ example: '2026-07-06' })

/**
 * FF-AUDIT-20260914-210459-072: a phone number as people write one — digits
 * with the usual punctuation (+ ( ) - . space, an "x"/"ext" extension), at
 * least six digits. Not a parser: the plant's reorder e-mail and dial links
 * carry what was typed; this only keeps "abc" out.
 */
export function isPhoneNumber(value: string): boolean {
  const trimmed = value.trim()
  if (!/^\+?[\d\s().-]+(?:\s*(?:x|ext\.?)\s*\d{1,6})?$/i.test(trimmed)) return false
  const digits = trimmed.replace(/\D/g, '').length
  return digits >= 6 && digits <= 20
}
export const PhoneNumber = z
  .string()
  .max(50)
  .refine(isPhoneNumber, { message: 'expected a phone number' })
  .openapi({ example: '+1 902 555 0100' })

/**
 * FF-AUDIT-072: a web address — an http(s) URL, or a bare host such as
 * "example.com" (the client renders it with https://). "notaurl" is not one.
 */
export function isWebsite(value: string): boolean {
  const trimmed = value.trim()
  if (/\s/.test(trimmed) || trimmed.includes('@')) return false
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed) && !/^https?:\/\//i.test(trimmed)) return false
  const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
  try {
    const url = new URL(candidate)
    return /^https?:$/.test(url.protocol) && /^[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(url.hostname)
  } catch {
    return false
  }
}
export const Website = z
  .string()
  .max(255)
  .refine(isWebsite, { message: 'expected a web address' })
  .openapi({ example: 'https://example.com' })

/** Server-generated human entity number, e.g. QC-2026-0142 (DATABASE_DESIGN §7). */
export const EntityNumber = z
  .string()
  .regex(/^[A-Z]{1,6}-\d{4}-\d{4,}$/)
  .openapi({ example: 'QC-2026-0142' })

/** Related-entity summary embedded in responses (API_SPEC_RULES §5 — no include= graphs). */
export const SummaryRef = z
  .object({
    id: Uuid,
    name: z.string(),
  })
  .openapi('SummaryRef')
export type SummaryRef = z.infer<typeof SummaryRef>

/**
 * The result of importing source facts in bulk (REPORTS_IMPLEMENTATION_PLAN
 * step 2), shared by supplier deliveries, lot production results, hours worked
 * and competency assessments.
 *
 * Per-row errors rather than an all-or-nothing 422. A 400-line CSV with two bad
 * rows is the normal case, and rejecting the file wholesale means the operator
 * re-uploads it repeatedly with no way to see which lines are wrong. Rows that
 * validate are applied; rows that do not are reported with their line number,
 * so `created + updated + failed` always equals the number of rows submitted
 * and nothing is silently dropped.
 */
export const SourceImportResult = z
  .object({
    created: z.number().int(),
    updated: z.number().int(),
    failed: z.number().int(),
    errors: z.array(z.object({ row: z.number().int(), message: z.string() })).max(1000),
  })
  .openapi('SourceImportResult')
export type SourceImportResult = z.infer<typeof SourceImportResult>

/** Soft polymorphic reference (MODULE_BLUEPRINT §2.3). */
export const EntityRef = z
  .object({
    entityType: z.string().min(1),
    entityId: Uuid,
  })
  .openapi('EntityRef')
export type EntityRef = z.infer<typeof EntityRef>
